import _ from 'lodash';
import fp from 'lodash/fp';
import React from 'react';
import { Grid as MuiGrid, styled, TextField, Tooltip } from '@mui/material';
import Numeral from 'numeral';

import classnames from 'classnames';
import { makeStyles } from '@mui/styles';

import { FakeBar } from './fakebar.chart';
import TooltipCheckbox from '../../../components/inputs/TooltipCheckbox';
import { Prime100Coverages } from '../../workflows/quotes/prime-100/types';

/**
 * V6-specific coverage chart for P100 read-only view.
 * No Social Engineering, Reverse Social Engineering, or Computer Funds Transfer Fraud.
 * Uses WEBSITE_MEDIA_CONTENT_LIABILITY_V6 and V6-specific coverages.
 */
export const CoverageChartV6 = ({
  data = {},
  options,
  renderContigentBusinessIncomeSF,
  renderContigentBusinessIncomeSFIT,
  renderfundsTransferHeldFundsValues,
  renderFtfSeSubValues,
  renderFtfSeCpSubValues,
  readOnly,
  renderinvoiceManipulationDeductibleValues,
  renderWebsiteMediaDeductibleValues,
}: any) => {
  const classes = styles({
    isRenewal: data.isRenewal,
    isMigration: data.isMigration,
  });

  const aggregateLimit = data.limit;

  const grouped = React.useMemo(() => {
    return fp.flow(
      fp.filter((coverage: any) => coverage),
      fp.sortBy('order'),
      fp.groupBy('groupName')
    )(options);
  }, [options]);

  return (
    <div className={classes.chartContainer}>
      <MuiGrid container>
        {Object.keys(grouped).map((groupName) => {
          return (
            <React.Fragment key={groupName}>
              <GroupHeader>{groupName}</GroupHeader>

              {Object.values(grouped[groupName]).map((option: any) => {
                const checkboxClasses = classnames({
                  mandatory: option.mandatory,
                });

                if (
                  option.id === 'contingentBusinessIncomeSF' &&
                  _.isFunction(renderContigentBusinessIncomeSF)
                ) {
                  return (
                    <React.Fragment key={option.id}>
                      {renderContigentBusinessIncomeSF({ classes })}
                    </React.Fragment>
                  );
                }
                if (
                  option.id === 'contingentBusinessIncomeSFIT' &&
                  _.isFunction(renderContigentBusinessIncomeSFIT)
                ) {
                  return (
                    <React.Fragment key={option.id}>
                      {renderContigentBusinessIncomeSFIT({ classes })}
                    </React.Fragment>
                  );
                }
                if (
                  option.id === 'ftfSeSubValues' &&
                  _.isFunction(renderFtfSeSubValues)
                ) {
                  return (
                    <React.Fragment key={option.id}>
                      {renderFtfSeSubValues({ classes })}
                    </React.Fragment>
                  );
                }
                if (
                  option.id === 'ftfSeCpSubValues' &&
                  _.isFunction(renderFtfSeCpSubValues)
                ) {
                  return (
                    <React.Fragment key={option.id}>
                      {renderFtfSeCpSubValues({ classes })}
                    </React.Fragment>
                  );
                }
                if (
                  option.id === 'fundsTransferHeldFunds' &&
                  _.isFunction(renderfundsTransferHeldFundsValues)
                ) {
                  return (
                    <React.Fragment key={option.id}>
                      {renderfundsTransferHeldFundsValues({ classes })}
                    </React.Fragment>
                  );
                }

                if (
                  option.id === 'invoiceManipulationDeductible' &&
                  _.isFunction(renderinvoiceManipulationDeductibleValues)
                ) {
                  return (
                    <React.Fragment key={option.id}>
                      {renderinvoiceManipulationDeductibleValues({ classes })}
                    </React.Fragment>
                  );
                }

                if (
                  option.id === 'websiteMediaContentLiabilityDeductible' &&
                  _.isFunction(renderWebsiteMediaDeductibleValues)
                ) {
                  return (
                    <React.Fragment key={option.id}>
                      {renderWebsiteMediaDeductibleValues({ classes })}
                    </React.Fragment>
                  );
                }

                if (option.id === 'ADDITIONAL_INSURED') {
                  const insureds = Array.isArray(option.checked)
                    ? option.checked
                    : data?.additionalInsureds ?? [];
                  const AIcount = insureds.length;
                  option.disabled = !!readOnly;

                  return (
                    <React.Fragment key={option.id}>
                      <MuiGrid item sm={7}>
                        <TooltipCheckbox
                          {...option}
                          disabled={true}
                          checked={AIcount > 0}
                          className={checkboxClasses}
                          label={
                            <Tooltip
                              title={option.tooltip ?? ''}
                              disableFocusListener
                            >
                              <span>
                                {option.label ?? 'Additional Insured'}
                              </span>
                            </Tooltip>
                          }
                          variant={!option.mandatory ? 'base' : 'filled'}
                        />
                      </MuiGrid>
                      <MuiGrid
                        container
                        item
                        sm={5}
                        justifyContent={
                          readOnly ? 'flex-start' : 'space-evenly'
                        }
                      >
                        <MuiGrid item sm={6} className="flex align-right">
                          <TextField
                            variant="standard"
                            name="additionalInsureds"
                            value={AIcount}
                            classes={{
                              root: classes.inputField,
                            }}
                            InputProps={{
                              classes: { input: classes.inputBase },
                            }}
                            disabled
                          />
                        </MuiGrid>
                      </MuiGrid>
                    </React.Fragment>
                  );
                }

                const isChecked =
                  option.checked === true || option.defaultChecked === true;

                return (
                  <MuiGrid container key={option.id}>
                    <MuiGrid item sm={7} className={classes.gridItem}>
                      <TooltipCheckbox
                        {...option}
                        disabled={readOnly ? !isChecked : option.disabled}
                        showRedTooltip={
                          readOnly ? false : option.showRedTooltip
                        }
                        className={checkboxClasses}
                        label={
                          <Tooltip title={option.tooltip} disableFocusListener>
                            <span>{option.label}</span>
                          </Tooltip>
                        }
                        variant={!option.mandatory ? 'base' : 'filled'}
                      />
                    </MuiGrid>

                    <MuiGrid item sm={5} className={classes.gridItem}>
                      <FakeBar
                        value={(option.percentage ?? 0) * 100}
                        aggregate={data.limit}
                        percentage={option.percentage ?? 0}
                        readOnly={readOnly}
                      />
                    </MuiGrid>

                    <div className={classes.chartAxis}>
                      <i
                        className={`${classes.axis} label-first label-left`}
                        data-label="0"
                      />
                      <i
                        className={`${classes.axis} label-last label-left`}
                        data-label={Numeral(aggregateLimit).format('0a')}
                      />
                    </div>
                  </MuiGrid>
                );
              })}
            </React.Fragment>
          );
        })}
      </MuiGrid>
    </div>
  );
};

const styles = makeStyles(({ config, palette }) => ({
  chartContainer: {
    position: 'relative',
  },

  chartAxis: {
    width: '41.666667%',
    height: '100%',
    position: 'absolute',
    top: 0,
    right: 0,
  },

  axis: {
    height: 'calc(100% - 1.5rem)',
    position: 'absolute',
    top: '-1.5rem',
    borderRight: '1px dashed #9492A4',
    fontSize: config.textSizes.petite,
    transition: 'left 300ms ease-out',

    '&:before': {
      content: 'attr(data-label)',
      position: 'absolute',
      lineHeight: 0.9,
      right: 5,
    },

    '&.label-first': {
      left: 0,
    },

    '&.label-last': {
      right: 0,
    },
  },

  gridItem: {
    display: 'flex',
    alignItems: 'center',
  },
  inputBase: {
    padding: '0.35rem !important',
    height: '1rem',
    textAlign: 'center',
    background: (props) => {
      // @ts-expect-error TS(2339): Property 'isRenewal' does not exist on type '{}'.
      if (props.isRenewal || props.isMigration) {
        return `${palette.drawer.background} !important`;
      }
    },
  },
  inputField: {
    width: '4rem',
    height: '2rem',
    textAlign: 'center',
    fontSize: '1.167rem',
    color: palette.primary.main,
    marginTop: '0.1rem',
  },
}));

const GroupHeader = styled(MuiGrid)(({ theme }) => ({
  padding: '2rem 0 1rem 2rem',
  color: theme.palette.primary.contrastText,
  fontSize: theme.config.textSizes.tertia,
  fontWeight: theme.config.weights.bold,
}));
